import React, { useEffect, useMemo, useState } from "react";
import { bondReadyAPI } from "../../services/api";
import BondReadyShell, {
  Card,
  Pill,
  br,
} from "./BondReadyShell";
import {
  UNKNOWN,
  deriveOperationalKpis,
  gate2BrokerLabel,
  humanDate,
  ownerPresentation,
  formatUsd,
  nextActionPresentation,
  needsAction,
  opportunityRelationshipLabel,
  opportunityTrackerView,
  readinessFromSnapshot,
  structuredFinancialAsOf,
  requestTypeSummary,
  workspaceReviewLabel,
  workflowLabel,
} from "./bondreadyPresentation";

export { CANONICAL_STAGES, qualityLabel, workflowLabel } from "./bondreadyPresentation";

type ApiRecord = Record<string, any>;

const FUNNEL_FIELDS: ReadonlyArray<readonly [string, string, string, string]> = [
  ["pipelineCandidates", "Trade Partners Identified", "Through ConstructPay GC relationships", ""],
  ["bondingDataPresent", "Bonding Data Available", "Surety capacity information present in source data", "blue"],
  ["gate1OutreachApproved", "Approved for ConstructPay Outreach", "GC-authorized ConstructPay contact", "green"],
  ["gate2Authorizations", "Contractor Authorizations", "Trade partner release approvals", "amber"],
  ["introductionsBrokered", "Introductions to Surety Partner", "Only after contractor authorization", "green"],
  ["unauthorizedDisclosures", "Unauthorized disclosures", "Hard-stop target remains zero", "red"],
];

function shown(input: any): string {
  if (input === null || input === undefined || input === "") return UNKNOWN;
  return String(input);
}

function metricDisplay(metric: any): string {
  if (metric === null || metric === undefined) return UNKNOWN;
  if (typeof metric === "number" || typeof metric === "string") return String(metric);
  if (typeof metric === "object") {
    if (metric.available === false) return "Unavailable / not supplied";
    if (metric.value === null || metric.value === undefined) return UNKNOWN;
    return String(metric.value);
  }
  return UNKNOWN;
}

function kpiValueColor(tone: string): React.CSSProperties {
  if (tone === "blue") return { color: "#7bb6ff" };
  if (tone === "green") return { color: "#62d288" };
  if (tone === "amber") return { color: "#f1c46d" };
  if (tone === "red") return { color: "#ff7b7b" };
  return {};
}

function statusTone(status: any): "green" | "blue" | "amber" | "red" {
  if (["CLOSED", "INTRODUCED_TO_EXTERNAL_PARTNER", "TRADE_PARTNER_APPROVED_INTRODUCTION", "TERMS_BOND_PATH_RECEIVED"].includes(status)) {
    return "green";
  }
  if (["DECLINED"].includes(status)) return "red";
  if ([
    "GC_APPROVED_FOR_CONSTRUCTPAY_OUTREACH",
    "CONSTRUCTPAY_OUTREACH_STARTED",
    "TRADE_PARTNER_REVIEWING",
    "PARTNER_REVIEWING",
    "DOCS_REQUESTED",
  ].includes(status)) {
    return "blue";
  }
  return "amber";
}

/**
 * Canonical deep link for an opportunity row.
 * Uses only bondready_opportunities.id — never org/visibility/request/fixture substitutes.
 */
export function opportunityDetailPath(opportunity: ApiRecord): string | null {
  const id = opportunity?.id;
  if (id === null || id === undefined || id === "") return null;
  return `/bondready/${String(id)}`;
}

export function sortPortfolioWorkQueue(opportunities: ApiRecord[]): ApiRecord[] {
  return [...opportunities].sort((a, b) => {
    const aNeeds = needsAction(a) ? 0 : 1;
    const bNeeds = needsAction(b) ? 0 : 1;
    if (aNeeds !== bNeeds) return aNeeds - bNeeds;
    return String(a.subjectName || "").localeCompare(String(b.subjectName || ""));
  });
}

function WorkQueueTable({ opportunities }: { opportunities: ApiRecord[] }) {
  const sorted = useMemo(() => sortPortfolioWorkQueue(opportunities), [opportunities]);

  const thStyle: React.CSSProperties = {
    padding: "10px 10px",
    borderBottom: "1px solid #263548",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };
  const tdStyle: React.CSSProperties = {
    padding: "12px 10px",
    verticalAlign: "top",
  };
  const stickyOpenHead: React.CSSProperties = {
    ...thStyle,
    position: "sticky",
    right: 0,
    background: "#121c29",
    zIndex: 2,
    textAlign: "right",
    minWidth: 88,
    boxShadow: "-8px 0 12px rgba(0,0,0,0.18)",
  };
  const stickyOpenCell: React.CSSProperties = {
    ...tdStyle,
    position: "sticky",
    right: 0,
    background: "#121c29",
    zIndex: 1,
    textAlign: "right",
    minWidth: 88,
    boxShadow: "-8px 0 12px rgba(0,0,0,0.18)",
  };

  return (
    <Card
      title="Portfolio work queue"
      subtitle={`${opportunities.length} opportunit${opportunities.length === 1 ? "y" : "ies"} visible to your organization — active BondReady work items. Select an opportunity to open it.`}
      badge={<Pill tone="purple">Primary</Pill>}
      testId="bondready-action-center-card"
    >
      {!opportunities.length ? (
        <div style={br.empty}>No BondReady opportunities are currently available to your organization.</div>
      ) : (
        <div style={{ overflowX: "auto" }} data-testid="bondready-work-queue">
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 13, tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
            </colgroup>
            <thead>
              <tr style={{ color: "#9fb0c4", textAlign: "left" }}>
                <th style={thStyle}>Company</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Lifecycle stage</th>
                <th style={thStyle}>Owner</th>
                <th style={thStyle}>Next action</th>
                <th style={thStyle}>Due</th>
                <th style={thStyle}>Requests</th>
                <th style={thStyle}>Attention</th>
                <th style={stickyOpenHead}>Open</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((opportunity) => {
                const tracker = opportunityTrackerView(opportunity.workflowStatus);
                const gate2 = gate2BrokerLabel(opportunity.gate2State);
                const workspace = workspaceReviewLabel(opportunity.viewerRole);
                const attention = needsAction(opportunity);
                const readiness = readinessFromSnapshot(opportunity.sourceSnapshotContext);
                const financialAsOf = structuredFinancialAsOf(opportunity);
                const detailPath = opportunityDetailPath(opportunity);
                return (
                  <tr
                    key={String(opportunity.id)}
                    data-testid={`bondready-pipeline-item-${opportunity.id}`}
                    style={{
                      background: attention ? "rgba(241, 196, 109, 0.06)" : "transparent",
                    }}
                  >
                    <td style={{ ...tdStyle, borderBottom: "1px solid #1d2a3a" }}>
                      {detailPath ? (
                        <a
                          href={detailPath}
                          data-testid={`bondready-open-${opportunity.id}`}
                          style={{ color: "#edf5ff", fontWeight: 700, textDecoration: "none" }}
                        >
                          {shown(opportunity.subjectName)}
                        </a>
                      ) : (
                        <strong style={{ color: "#edf5ff" }}>{shown(opportunity.subjectName)}</strong>
                      )}
                      {(opportunity.fundingAmount !== null && opportunity.fundingAmount !== undefined) ||
                      (opportunity.bondAmount !== null && opportunity.bondAmount !== undefined) ? (
                        <div style={{ marginTop: 8, color: "#9fb0c4", fontSize: 12, lineHeight: 1.45 }}>
                          {(opportunity.fundingAmount !== null && opportunity.fundingAmount !== undefined) && (
                            <div>
                              WC {formatUsd(opportunity.fundingAmount)}
                              {opportunity.fundingDetails ? ` · ${opportunity.fundingDetails}` : ""}
                            </div>
                          )}
                          {(opportunity.bondAmount !== null && opportunity.bondAmount !== undefined) && (
                            <div>
                              Bond {formatUsd(opportunity.bondAmount)}
                              {opportunity.bondProjectName
                                ? ` · ${opportunity.bondProjectName}`
                                : opportunity.bondDetails ? ` · ${opportunity.bondDetails}` : ""}
                            </div>
                          )}
                          <div>Financials through {humanDate(financialAsOf)}</div>
                        </div>
                      ) : financialAsOf ? (
                        <div style={{ marginTop: 8, color: "#9fb0c4", fontSize: 12 }}>
                          Financials through {humanDate(financialAsOf)}
                        </div>
                      ) : null}
                      <div
                        style={{ marginTop: 8, display: "grid", gap: 4 }}
                        data-testid={`bondready-auth-compact-${opportunity.id}`}
                      >
                        <Pill tone={workspace.tone}>{workspace.label}</Pill>
                        <Pill tone={gate2.tone}>{gate2.label}</Pill>
                      </div>
                      <span data-testid={`bondready-status-raw-${opportunity.id}`} style={{ display: "none" }}>
                        {shown(opportunity.workflowStatus)}
                      </span>
                      <span data-testid={`bondready-gate2-raw-${opportunity.id}`} style={{ display: "none" }}>
                        {shown(opportunity.gate2State)}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, borderBottom: "1px solid #1d2a3a", color: "#7bb6ff", fontWeight: 700 }}>
                      {opportunityRelationshipLabel(opportunity)}
                    </td>
                    <td style={{ ...tdStyle, borderBottom: "1px solid #1d2a3a" }}>
                      <Pill tone={statusTone(opportunity.workflowStatus)}>
                        {tracker.declined ? "Declined" : tracker.currentLabel}
                      </Pill>
                    </td>
                    <td style={{ ...tdStyle, borderBottom: "1px solid #1d2a3a" }}>
                      {ownerPresentation(opportunity.currentOwner)}
                    </td>
                    <td style={{ ...tdStyle, borderBottom: "1px solid #1d2a3a", color: "#d7e6f7", overflowWrap: "anywhere" as const }}>
                      {nextActionPresentation(opportunity.nextAction, opportunity.subjectName)}
                    </td>
                    <td style={{ ...tdStyle, borderBottom: "1px solid #1d2a3a", whiteSpace: "nowrap" }}>
                      {opportunity.nextActionDueAt ? humanDate(opportunity.nextActionDueAt) : "Not set"}
                    </td>
                    <td style={{ ...tdStyle, borderBottom: "1px solid #1d2a3a" }}>
                      {requestTypeSummary(opportunity)}
                    </td>
                    <td style={{ ...tdStyle, borderBottom: "1px solid #1d2a3a" }} data-testid={`bondready-attention-${opportunity.id}`}>
                      {attention ? <Pill tone="amber">Needs action</Pill> : <span style={{ color: "#6f8095" }}>—</span>}
                    </td>
                    <td style={{ ...stickyOpenCell, borderBottom: "1px solid #1d2a3a", background: attention ? "#1a2218" : "#121c29" }}>
                      {detailPath ? (
                        <a
                          href={detailPath}
                          data-testid={`bondready-row-open-${opportunity.id}`}
                          style={{ ...br.btn, ...br.btnPrimary, padding: "8px 12px", fontSize: 12 }}
                        >
                          Open
                        </a>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function OperationalKpis({ opportunities }: { opportunities: ApiRecord[] }) {
  const kpis = deriveOperationalKpis(opportunities);
  const cards: Array<[string, number, string]> = [
    ["Active opportunities", kpis.activeOpportunities, "Persisted BondReady opportunities"],
    ["Needs action", kpis.needsAction, "Awaiting review or outreach progress"],
    ["Requests in progress", kpis.requestsInProgress, "Opportunities with recorded requests"],
    ["Awaiting partner", kpis.awaitingPartner, "Trade partner or partner review stages"],
  ];
  return (
    <section aria-label="Operational summary" data-testid="bondready-operational-kpis" style={{ marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 }} className="bondready-ops-kpis">
        {cards.map(([label, value, sub]) => (
          <div key={label} style={br.kpi}>
            <div style={br.kpiLabel}>{label}</div>
            <div style={br.kpiValue}>{value}</div>
            <div style={br.kpiSub}>{sub}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function GcFunnelSecondary({ summary }: { summary: ApiRecord | null }) {
  return (
    <Card
      title="Potential Opportunities"
      subtitle="Trade partners identified through ConstructPay's GC relationships that may become future bonding opportunities. Contractor authorization is required before company-specific information is shared."
      badge={<Pill tone="blue">Secondary context</Pill>}
      testId="bondready-funnel"
      style={{ ...br.sectionPad, marginTop: 28 }}
    >
      <details data-testid="bondready-funnel-details">
        <summary style={{ cursor: "pointer", color: "#7bb6ff", fontWeight: 700 }}>
          Show potential opportunity context
        </summary>
        <p style={{ ...br.muted, marginTop: 12 }} data-testid="bondready-funnel-story">
          These are potential future opportunities from ConstructPay GC relationships — not active BondReady portfolio opportunities. Bonding data available reflects surety capacity information present in source data, not qualification or referral status.
        </p>
        {!summary ? (
          <div style={br.muted}>Loading potential-opportunity metrics...</div>
        ) : (
          <div className="bondready-kpi-grid" style={{ ...br.kpiGrid, marginBottom: 0, marginTop: 12 }} data-testid="bondready-funnel-metrics">
            {FUNNEL_FIELDS.map(([metricKey, label, sub, tone]) => (
              <React.Fragment key={metricKey}>
                <div style={br.kpi}>
                  <div style={br.kpiLabel}>{label}</div>
                  <div
                    style={{ ...br.kpiValue, fontSize: 22, ...kpiValueColor(tone) }}
                    data-testid={`bondready-funnel-${metricKey}`}
                  >
                    {metricDisplay(summary[metricKey])}
                  </div>
                  <div style={br.kpiSub}>{sub}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
        <p style={{ ...br.muted, marginBottom: 0, marginTop: 12 }}>
          Company-specific external disclosure remains locked unless the applicable Gate 2 authorization has been recorded. No automated external sharing. No company-specific pre-authorization disclosure.
        </p>
      </details>
    </Card>
  );
}

function partnerActivityLaneKey(opportunity: ApiRecord): string {
  const status = opportunity.workflowStatus;
  if (!status || status === "NOT_REVIEWED") return "review";
  if ([
    "GC_APPROVED_FOR_CONSTRUCTPAY_OUTREACH",
    "CONSTRUCTPAY_OUTREACH_STARTED",
    "TRADE_PARTNER_REVIEWING",
  ].includes(status)) return "outreach";
  if ([
    "TRADE_PARTNER_APPROVED_INTRODUCTION",
    "INTRODUCED_TO_EXTERNAL_PARTNER",
    "PARTNER_REVIEWING",
    "DOCS_REQUESTED",
    "TERMS_BOND_PATH_RECEIVED",
  ].includes(status)) return "partner";
  if (["CLOSED", "DECLINED"].includes(status)) return "closed";
  return "review";
}

function PartnerTracker({ opportunities }: { opportunities: ApiRecord[] }) {
  const lanes = [
    { key: "review", title: "Needs review" },
    { key: "outreach", title: "Outreach / review" },
    { key: "partner", title: "Partner review" },
    { key: "closed", title: "Closed / declined" },
  ];

  return (
    <Card
      title="Partner Activity"
      subtitle="One card per opportunity by current lifecycle stage. Open an opportunity to advance, decline, or review history."
      badge={<Pill tone="blue">Lifecycle board</Pill>}
      testId="bondready-partner-tracker"
    >
      <div className="bondready-kanban" style={br.kanban}>
        {lanes.map((lane) => {
          const items = opportunities.filter((opportunity) => partnerActivityLaneKey(opportunity) === lane.key);
          return (
            <div key={lane.key} style={br.lane} data-testid={`bondready-partner-lane-${lane.key}`}>
              <h4 style={br.laneHead}>{lane.title}</h4>
              {items.length === 0 ? (
                <div style={br.empty}>None in this stage</div>
              ) : (
                items.map((opportunity) => (
                  <div
                    key={opportunity.id}
                    style={{ ...br.ticket, margin: 10 }}
                    data-testid={`bondready-partner-ticket-${opportunity.id}`}
                  >
                    <strong style={{ fontSize: 12 }}>{shown(opportunity.subjectName)}</strong>
                    <p style={{ ...br.muted, margin: "6px 0" }}>{workflowLabel(opportunity.workflowStatus)}</p>
                    <p style={{ ...br.muted, margin: 0, overflowWrap: "anywhere" as const }}>
                      {nextActionPresentation(opportunity.nextAction, opportunity.subjectName)}
                    </p>
                    {Number(opportunity.requestCount) > 0 ? (
                      <p style={{ ...br.muted, margin: "6px 0 0" }}>
                        {Number(opportunity.requestCount)} request{Number(opportunity.requestCount) === 1 ? "" : "s"} inside this opportunity
                      </p>
                    ) : null}
                    <div style={{ marginTop: 8 }}>
                      {opportunityDetailPath(opportunity) ? (
                        <a
                          href={opportunityDetailPath(opportunity)!}
                          style={{ ...br.btn, padding: "7px 10px", fontSize: 11 }}
                        >
                          Review
                        </a>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function BondReadyPipeline() {
  const [opportunities, setOpportunities] = useState<ApiRecord[] | null>(null);
  const [summary, setSummary] = useState<ApiRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const section = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("section") || "pipeline";
    } catch {
      return "pipeline";
    }
  }, []);
  const showTracker = section === "tracker";

  useEffect(() => {
    let active = true;
    Promise.all([
      (bondReadyAPI as any).listOpportunities(),
      (bondReadyAPI as any).getPipelineSummary().catch(() => ({ data: null })),
    ])
      .then(async ([opportunityResponse, summaryResponse]: any[]) => {
        if (!active) return;
        const listed = Array.isArray(opportunityResponse.data) ? opportunityResponse.data : [];
        setSummary(summaryResponse?.data && typeof summaryResponse.data === "object" ? summaryResponse.data : {});

        const needsHydration = listed.filter(
          (item: ApiRecord) =>
            Number(item.requestCount) > 0 &&
            item.fundingAmount == null &&
            item.bondAmount == null &&
            typeof (bondReadyAPI as any).getOpportunity === "function"
        );
        if (!needsHydration.length) {
          setOpportunities(listed);
          return;
        }

        const hydrated = await Promise.all(
          listed.map(async (item: ApiRecord) => {
            if (Number(item.requestCount) <= 0 || item.fundingAmount != null || item.bondAmount != null) {
              return item;
            }
            try {
              const detail = await (bondReadyAPI as any).getOpportunity(item.id);
              const requests = Array.isArray(detail?.data?.requests) ? detail.data.requests : [];
              const funding = requests.find((r: ApiRecord) => r.requestType === "FUNDING");
              const bond = requests.find((r: ApiRecord) => r.requestType === "BOND");
              return {
                ...item,
                fundingAmount: funding?.amount ?? item.fundingAmount,
                fundingDetails: funding?.details ?? item.fundingDetails,
                bondAmount: bond?.amount ?? item.bondAmount,
                bondDetails: bond?.details ?? item.bondDetails,
              };
            } catch {
              return item;
            }
          })
        );
        if (active) setOpportunities(hydrated);
      })
      .catch((caught: any) => {
        if (active) setError(caught?.response?.data?.error || caught?.message || "Unable to load BondReady opportunities");
      });
    return () => { active = false; };
  }, []);

  if (error) {
    return (
      <BondReadyShell
        activeNav="pipeline"
        title="BondReady Opportunities"
        subtitle="Unable to load BondReady opportunities."
      >
        <div role="alert" data-testid="bondready-pipeline">Error: {error}</div>
      </BondReadyShell>
    );
  }

  if (!opportunities) {
    return (
      <BondReadyShell
        activeNav={showTracker ? "tracker" : "pipeline"}
        title="BondReady Portfolio"
        subtitle="Loading portfolio..."
      >
        <div data-testid="bondready-pipeline">Loading BondReady portfolio...</div>
      </BondReadyShell>
    );
  }

  return (
    <div data-testid="bondready-pipeline">
      <BondReadyShell
        activeNav={showTracker ? "tracker" : "pipeline"}
        title="BondReady Portfolio"
        subtitle="What opportunities require attention, where each one sits in the lifecycle, and what must happen next."
      >
        {showTracker ? (
          <PartnerTracker opportunities={opportunities} />
        ) : (
          <>
            <OperationalKpis opportunities={opportunities} />

            <WorkQueueTable opportunities={opportunities} />

            <GcFunnelSecondary summary={summary} />
          </>
        )}
      </BondReadyShell>
    </div>
  );
}
